import { ComponentType, SVGProps, useEffect, useState } from "react";
import { BottomNavigationItem } from "@/components/navigation/mobile/bottom-navigation/BottomNavigationItem";
import { useRouter, useNavigate } from "@tanstack/react-router";
import { CreateSelectionBottomSheet } from "@/components/create/CreateSelectionBottomSheet";

import { CreateProductFlow } from "@/components/create/product/CreateProductFlow";
import { CreateCollectionFlow } from "@/components/create/collection/CreateCollectionFlow";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUserSummary } from "@/hooks/api/useUsers";
import { useBottomNav } from "@/contexts/BottomNavContext";
import { useQueryClient } from "@tanstack/react-query";

import HomeIcon from "@/icons/home";
import MagnifyingIcon from "@/icons/magnifying";
import PlusIcon from "@/icons/plus";

export function BottomNavigation() {
  const {
    state: { location },
  } = useRouter();
  const navigate = useNavigate();

  const [createOptionsOpen, setCreateOptionsOpen] = useState(false);
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // Read nav height from context
  const { bottomNavHeight } = useBottomNav();

  // Get current user
  const { user, loading: userLoading } = useCurrentUser();
  const userId = user?.uid;
  // Fetch user summary (avatarUrl)
  const { data: userSummary, isLoading: summaryLoading } =
    useUserSummary(userId);

  // Compute Tailwind height class from numeric height unit
  const heightTw = `h-${bottomNavHeight}`;

  // Build profile link and fallback avatar
  const profileImage = userSummary?.avatarUrl || "empty-image";
  const profilePath = userId ? `/profiles/${userId}` : "/";
  // React Query client for invalidating queries
  const queryClient = useQueryClient();

  // Handle create selection
  const handleOptionSelect = (option: "collection" | "look" | "product") => {
    setCreateOptionsOpen(false);
    if (option === "product") {
      setNewProductOpen(true);
    }
    if (option === "look") {
      navigate({ to: "/looks/create", search: { collectionId: undefined, from: undefined } });
    }
    if (option === "collection") {
      setNewCollectionOpen(true);
    }
  };

  // Handle post-create navigation
  const handleNewProductCreated = (newId: string) => {
    setNewProductOpen(false);
    navigate({ to: "/products/$id", params: { id: newId } });
  };

  useEffect(() => {
    if (newProductOpen) {
      setIsCreating(false);
    }
  }, [newProductOpen]);

  useEffect(() => {
    if (!createOptionsOpen) {
      const timeout = setTimeout(() => {
        setIsCreating(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [createOptionsOpen]);

  const currentPath = location.pathname;
  const navClass = `fixed bottom-0 left-0 right-0 pb-safe bg-white ${heightTw} max-w-md mx-auto border-t border-black-6 flex justify-between z-20 px-3 md:hidden${isCreating ? " z-50" : ""}`;

  const items = [
    {
      key: "/",
      icon: HomeIcon,
      label: "Featured",
      to: "/",
      requiresAuth: false,
      onClick: async () => {
        if (currentPath === "/") {
          // Get current scroll position
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const isAtTop = scrollTop <= 10; // 10px threshold

          if (!isAtTop) {
            // Not at top - smooth scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            // Already at top - refresh the feed based on active tab
            // Get the current tab from URL search params
            const urlParams = new URLSearchParams(window.location.search);
            const activeTab = urlParams.get('tab') || 'following'; // Default to following

            if (activeTab === 'discover') {
              // Cancel ongoing queries and reset discover feed
              await queryClient.cancelQueries({
                queryKey: ['feeds', 'discover'],
                exact: false,
              });
              await queryClient.resetQueries({
                queryKey: ['feeds', 'discover', 'infinite'],
                exact: false,
              });
            } else {
              // Cancel ongoing queries and reset following feed
              await queryClient.cancelQueries({
                queryKey: ['feeds', 'following'],
                exact: false,
              });
              await queryClient.resetQueries({
                queryKey: ['feeds', 'following', 'infinite'],
                exact: false,
              });
            }
          }
        } else {
          navigate({ to: "/" });
        }
      },
    },
    {
      key: "search",
      icon: MagnifyingIcon,
      label: "Search",
      to: "/search",
      requiresAuth: true,
    },
    {
      key: "create",
      icon: PlusIcon,
      label: "Create",
      onClick: () => {
        setCreateOptionsOpen(true);
        setIsCreating(true);
      },
      requiresAuth: true,
      asButton: true,
    },
    {
      key: "profile",
      profileImage: userLoading || summaryLoading ? undefined : profileImage,
      label: "Profile",
      to: profilePath,
      requiresAuth: true,
    },
  ];


  return (
    <>
      <nav aria-label="Bottom Navigation" className={navClass}>
        {items.map((item) => (
          <BottomNavigationItem
            key={item.key}
            icon={item?.icon as ComponentType<SVGProps<SVGSVGElement>>}
            selected={item.to === currentPath}
            profileImage={item.profileImage}
            label={item.label}
            to={item.to}
            onClick={item.onClick}
            requiresAuth={item.requiresAuth}
            asButton={item.asButton}
          />
        ))}
      </nav>

      <section className="fixed">
        {/* Create options bottom sheet */}
        <CreateSelectionBottomSheet
          open={createOptionsOpen}
          onClose={() => setCreateOptionsOpen(false)}
          onOptionSelect={handleOptionSelect}
        />

        {/* New product bottom sheets */}
        {newProductOpen && (
          <CreateProductFlow
            onClose={() => setNewProductOpen(false)}
            onProductCreated={handleNewProductCreated}
          />
        )}

        {/* New collection flow */}
        {newCollectionOpen && (
          <CreateCollectionFlow
            open={newCollectionOpen}
            onClose={() => setNewCollectionOpen(false)}
            onCollectionCreated={(id) => {
              setNewCollectionOpen(false);
              navigate({ to: `/collections/${id}/edit`, replace: true });
            }}
          />
        )}
      </section>
    </>
  );
}